/// <amd-module name="Analytics/Shared/Common/Utils/DateAdjuster" />
import * as CommonTypes from "Analytics/Shared/Common/Types";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";
import { addDays, convertDateToDateString, getDate, shiftToUTC } from "Analytics/Shared/Utils/DateUtils";

export interface IDataForDate<T> {
    date: string;
    data: T | null | undefined;
    customData?: any;
}

export function parseReturnDateIntoDisplayForm(trendBy: CommonTypes.TrendBy, record: any): string {
    switch (trendBy) {
        case CommonTypes.TrendBy.Days:
            return DateSKParser.parseDateSKAsDateString(record.CompletedDateSK);
        case CommonTypes.TrendBy.Weeks:
            return DateSKParser.convertDateToDateString(new Date(record.Date.WeekStartingDate)); // Date comes in string for like 'yyyy-mm-ddT00:00:00Z'
        default:
            return "";
    }
}

const defaultDatePropertyName = "CompletedDateSK";

export function getDateFilter(trendBy: CommonTypes.TrendBy, period: CommonTypes.Period, datePropertyName?: string): string {
    let lookBackDate = getLookBackDate(trendBy, period);
    const lookBackDateSK = DateSKParser.dateStringToDateSK(DateSKParser.convertDateToDateString(lookBackDate));
    datePropertyName = datePropertyName ? datePropertyName : defaultDatePropertyName;
    return `${datePropertyName} ge ${lookBackDateSK}`;
}

export function getLookBackDate(trendBy: CommonTypes.TrendBy, period: CommonTypes.Period): Date {
    const nowInUTC: Date = shiftToUTC(getDate()); // Convert client time to UTC.
    let lookBackDate: Date = nowInUTC;
    switch (trendBy) {
        case CommonTypes.TrendBy.Days:
            lookBackDate = addDays(nowInUTC, -getPeriodValueFromPeriod(period) + 1);
            break;
        case CommonTypes.TrendBy.Weeks:
            lookBackDate = addDays(nowInUTC, -7 * getPeriodValueFromPeriod(period) + 1);
            break;
        case CommonTypes.TrendBy.Hours:
            lookBackDate = addDays(getDate(), -getPeriodValueFromPeriod(period));
            break;
    }

    return lookBackDate;
}

export function adjustDataForAllPeriod<T>(
    data: IDataForDate<T>[],
    trendBy?: CommonTypes.TrendBy,
    period?: CommonTypes.Period,
    defaultData?: T | null
): IDataForDate<T>[] {
    if (!data || (data.length <= 0 && defaultData == null)) {
        return data;
    }

    // Creating dictionary of trend date
    const dataDayToAggrMap: { [key: string]: T | null | undefined } = {};
    const dateToCustomDataMap: { [key: string]: any } = {};
    data.forEach((d) => {
        if (!dataDayToAggrMap[d.date]) {
            dataDayToAggrMap[d.date] = d.data;
            dateToCustomDataMap[d.date] = d.customData;
        }
    });

    let dateDiff = 0;
    let dateStart: Date = getDate();

    const periodDays = getPeriodValueFromPeriod(period || CommonTypes.Period.Days_14);

    switch (trendBy) {
        case CommonTypes.TrendBy.Days:
            dateDiff = 1;
            dateStart = addDays(shiftToUTC(getDate()), dateDiff * (-periodDays + 1), true);
            break;
        case CommonTypes.TrendBy.Weeks:
            dateDiff = 7; // For weeks trendBy, period is # of weeks.
            const date = addDays(shiftToUTC(getDate()), dateDiff * (-periodDays + 1), true);
            dateStart = addDays(date, -date.getDay(), true);
            break;
    }

    const adjustedData: IDataForDate<T>[] = [];

    for (let eachDay = 1; eachDay <= periodDays; eachDay++) {
        const displayDate: string = convertDateToDateString(dateStart); // TODO: Utils_Date.format(dateStart, "yyyy-MM-dd");       //Expect data by API to be coming in this format.
        if (dataDayToAggrMap.hasOwnProperty(displayDate)) {
            adjustedData.push({
                date: convertDateToDateString(dateStart),
                data: dataDayToAggrMap[displayDate],
                customData: dateToCustomDataMap[displayDate]
            });
        } else {
            adjustedData.push({ date: convertDateToDateString(dateStart), data: defaultData });
        }

        // Increment day by one/seven depending upon trendBy.
        dateStart = addDays(dateStart, dateDiff);
    }

    return adjustedData;
}

export function getPeriodValueFromPeriod(period: CommonTypes.Period): number {
    switch (period) {
        case CommonTypes.Period.Days_1:
            return 1;
        case CommonTypes.Period.Days_7:
            return 7;
        case CommonTypes.Period.Days_14:
            return 14;
        case CommonTypes.Period.Days_30:
            return 30;
        case CommonTypes.Period.Days_180:
            return 180;
        default:
            return 30;
    }
}

export function getPeriodStartDate(trendBy: CommonTypes.TrendBy, period: CommonTypes.Period): Date {
    const nowInUTC: Date = shiftToUTC(getDate()); // Convert client time to UTC.
    let startDate: Date = nowInUTC;
    switch (trendBy) {
        case CommonTypes.TrendBy.Days:
            startDate = addDays(nowInUTC, -getPeriodValueFromPeriod(period) + 1);
            break;
        case CommonTypes.TrendBy.Weeks:
            startDate = addDays(nowInUTC, -7 * getPeriodValueFromPeriod(period) + 1);
            break;
    }
    return startDate;
}

export function dateDiffInDays(a: Date, b: Date): number {
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    return Math.floor((utc2 - utc1) / millisecondsPerDay);
}
